import { useEffect, useRef } from "react";
import { ProductCategoryDropdownOption } from "~/api/models/ProductCategory";
import { useFetchProducts } from "~/api/salesForce/useFetchProducts";
import { useBundles } from "~/hooks/useBundles";
import useDefaultProduct from "~/hooks/useDefaultProduct";
import useHairProducts from "~/hooks/useHairProducts";
import { UseInitializeProductsArgs } from "~/hooks/useInitializeProducts";
import useOpportunityProducts from "~/hooks/useOpportunityProducts";
import useTreatmentType from "~/hooks/useTreatmentType";
import { Product } from "~/stores/checkout/product";
import { HAIR_FAMILIES } from "~/stores/checkout/product.constants";
import { useCheckoutStore } from "~/stores/checkout/useCheckoutStore";

const getNewSelectedProducts = (params: {
  currentSelectedProducts: Product[];
  htSelected: ProductCategoryDropdownOption | undefined;
  allProducts: ReturnType<typeof useFetchProducts>["data"];
  transplantationProduct: Product | undefined;
  prpSelected: ProductCategoryDropdownOption | undefined;
  prpProduct: Product | undefined;
  freePrpProduct: Product | undefined;
  transplantOpportunityProduct: Product | undefined;
  prpOpportunityProduct: Product | undefined;
  freePrpOpportunityProduct: Product | undefined;
  transplantationDefaultProduct: Product | undefined;
  prpDefaultProduct: Product | undefined;
  bundleDefaultProduct: Product | undefined;
  eyebrowSelected: ProductCategoryDropdownOption | undefined;
  eyebrowProduct: Product | undefined;
  eyebrowOpportunityProduct: Product | undefined;
  eyebrowDefaultProduct: Product | undefined;
  beardSelected: ProductCategoryDropdownOption | undefined;
  beardProduct: Product | undefined;
  beardOpportunityProduct: Product | undefined;
  beardDefaultProduct: Product | undefined;
  mesotherapySelected: ProductCategoryDropdownOption | undefined;
  mesotherapyProduct: Product | undefined;
  mesotherapyOpportunityProduct: Product | undefined;
  freeMesotherapyOpportunityProduct: Product | undefined;
  mesotherapyDefaultProduct: Product | undefined;
}) => {
  // Preserve non-hair products
  const nonHairProducts = params.currentSelectedProducts.filter(
    (product) => !HAIR_FAMILIES.includes(product.family)
  );

  // Work only with hair products
  let hairProducts = params.currentSelectedProducts.filter((product) =>
    HAIR_FAMILIES.includes(product.family)
  );

  const transplantationProduct = params.transplantationProduct;
  const transplantOpportunityProduct = params.transplantOpportunityProduct;
  const transplantationDefaultProduct = params.transplantationDefaultProduct;
  const prpProduct = params.prpProduct;
  const freePrpProduct = params.freePrpProduct;
  const eyebrowProduct = params.eyebrowProduct;
  const beardProduct = params.beardProduct;
  const freePrpOpportunityProduct = params.freePrpOpportunityProduct;
  const mesotherapyProduct = params.mesotherapyProduct;
  const freeMesotherapyOpportunityProduct =
    params.freeMesotherapyOpportunityProduct;

  if (
    params.htSelected &&
    params.allProducts?.hair.head.transplantation.short &&
    !transplantationProduct
  ) {
    if (transplantOpportunityProduct || transplantationDefaultProduct) {
      hairProducts.push(
        transplantOpportunityProduct ?? transplantationDefaultProduct!
      );
    }
  } else if (!params.htSelected && transplantationProduct) {
    // remove product from products list
    hairProducts = hairProducts.filter(
      (v) => v.id !== transplantationProduct.id
    );
  }

  if (params.prpSelected && !prpProduct) {
    // Check if we already have any PRP product (free or regular) in the working hairProducts array
    const hasPrpInHairProducts = hairProducts.some(
      (product) => product.family === "prp hair"
    );

    // Prioritize free PRP if it exists
    if (freePrpOpportunityProduct && !hasPrpInHairProducts) {
      hairProducts.push(freePrpOpportunityProduct);
    } else if (params.prpOpportunityProduct && !hasPrpInHairProducts) {
      hairProducts.push(params.prpOpportunityProduct);
    } else if (!hasPrpInHairProducts) {
      // Only add default if no PRP products exist at all
      if (!params.htSelected && params.prpDefaultProduct) {
        hairProducts.push(params.prpDefaultProduct);
      } else if (params.htSelected && params.bundleDefaultProduct) {
        hairProducts.push(params.bundleDefaultProduct);
      }
    }
  } else if (!params.prpSelected && prpProduct) {
    hairProducts = hairProducts.filter((v) => v.id !== prpProduct.id);
  } else if (
    params.htSelected &&
    params.prpSelected &&
    // check it's selected because of prp not a free prp
    prpProduct &&
    prpProduct?.type !== "bundle" &&
    params.bundleDefaultProduct &&
    !freePrpProduct // Don't replace if there's already a free PRP
  ) {
    //remove old prp product
    hairProducts = hairProducts.filter((v) => v.id !== prpProduct?.id);
    //push new
    hairProducts.push(
      params.allProducts.hair.head.bundle?.find(
        (v) => v.sessions === prpProduct?.sessions
      ) ?? params.bundleDefaultProduct
    );
  } else if (
    params.prpSelected &&
    !params.htSelected &&
    // check it's selected because of prp not a free prp
    prpProduct &&
    prpProduct?.type === "bundle" &&
    params.prpDefaultProduct &&
    !freePrpProduct // Don't replace if there's already a free PRP
  ) {
    //remove old prp product
    hairProducts = hairProducts.filter((v) => v.id !== prpProduct?.id);
    // push new
    hairProducts.push(
      params.allProducts.hair.head.prp?.find(
        (v) => v.sessions === prpProduct?.sessions
      ) ?? params.prpDefaultProduct
    );
  }

  if (
    params.eyebrowSelected &&
    params.eyebrowDefaultProduct &&
    !eyebrowProduct
  ) {
    hairProducts.push(
      params.eyebrowOpportunityProduct ?? params.eyebrowDefaultProduct
    );
  } else if (!params.eyebrowSelected && eyebrowProduct) {
    hairProducts = hairProducts.filter((v) => v.id !== eyebrowProduct.id);
  }

  if (params.beardSelected && params.beardDefaultProduct && !beardProduct) {
    hairProducts.push(
      params.beardOpportunityProduct ?? params.beardDefaultProduct
    );
  } else if (!params.beardSelected && beardProduct) {
    hairProducts = hairProducts.filter((v) => v.id !== beardProduct.id);
  }

  if (
    params.mesotherapySelected &&
    params.mesotherapyDefaultProduct &&
    !mesotherapyProduct
  ) {
    // Check if we already have any mesotherapy product (free or regular) in the working hairProducts array
    const hasMesotherapyInHairProducts = hairProducts.some(
      (product) => product.family === "mesotherapy"
    );

    // Prioritize free mesotherapy if it exists
    if (freeMesotherapyOpportunityProduct && !hasMesotherapyInHairProducts) {
      hairProducts.push(freeMesotherapyOpportunityProduct);
    } else if (
      params.mesotherapyOpportunityProduct &&
      !hasMesotherapyInHairProducts
    ) {
      hairProducts.push(params.mesotherapyOpportunityProduct);
    } else if (!hasMesotherapyInHairProducts) {
      // Only add default if no mesotherapy products exist at all
      hairProducts.push(params.mesotherapyDefaultProduct);
    }
  } else if (!params.mesotherapySelected && mesotherapyProduct) {
    hairProducts = hairProducts.filter((v) => v.id !== mesotherapyProduct.id);
  }

  // Return both together
  const result = [...nonHairProducts, ...hairProducts];
  return result;
};

type UseInitializeHairProductsArgs = UseInitializeProductsArgs;

const useInitializeHairProducts = ({
  loadingOpportunity,
  opportunity,
  onComplete,
}: UseInitializeHairProductsArgs) => {
  const hasInitialized = useRef(false);
  const lastOpportunityId = useRef<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useCheckoutStore((state) => [
    state.selectedProducts,
    state.setSelectedProducts,
  ]);
  const {
    htSelected,
    prpSelected,
    eyebrowSelected,
    beardSelected,
    mesotherapySelected,
  } = useTreatmentType();
  const { data: allProducts, isLoading } = useFetchProducts();
  const {
    prpOpportunityProduct,
    beardOpportunityProduct,
    eyebrowOpportunityProduct,
    transplantOpportunityProduct,
    freePrpOpportunityProduct,
    mesotherapyOpportunityProduct,
    freeMesotherapyOpportunityProduct,
  } = useOpportunityProducts(opportunity);
  const {
    transplantationProduct,
    prpProduct,
    eyebrowProduct,
    beardProduct,
    freePrpProduct,
    mesotherapyProduct,
  } = useHairProducts();
  const { selectedBundles } = useBundles();

  const [
    prpDefaultProduct,
    bundleDefaultProduct,
    transplantationDefaultProduct,
    eyebrowDefaultProduct,
    beardDefaultProduct,
    mesotherapyDefaultProduct,
  ] = [
    useDefaultProduct(allProducts?.hair.head.prp),
    useDefaultProduct(allProducts?.hair.head.bundle),
    useDefaultProduct(allProducts?.hair.head.transplantation.short),
    useDefaultProduct(allProducts?.hair.eyebrow.transplantation),
    useDefaultProduct(allProducts?.hair.beard.transplantation),
    useDefaultProduct(allProducts?.hair.head.mesotherapy),
  ];

  // Reset initialization when opportunity changes
  useEffect(() => {
    if (opportunity?.id !== lastOpportunityId.current) {
      hasInitialized.current = false;
      lastOpportunityId.current = opportunity?.id || null;
    }
  }, [opportunity?.id]);

  useEffect(() => {
    // Only initialize once per opportunity and when there are opportunity products
    const hasOpportunityHairProducts = !!(
      transplantOpportunityProduct ||
      prpOpportunityProduct ||
      freePrpOpportunityProduct ||
      eyebrowOpportunityProduct ||
      beardOpportunityProduct ||
      mesotherapyOpportunityProduct ||
      freeMesotherapyOpportunityProduct
    );

    if (
      !hasInitialized.current &&
      !isLoading &&
      !loadingOpportunity &&
      hasOpportunityHairProducts
    ) {
      const products = getNewSelectedProducts({
        currentSelectedProducts: selectedProducts,
        htSelected,
        allProducts,
        transplantationProduct,
        prpSelected,
        prpProduct,
        freePrpProduct,
        transplantOpportunityProduct,
        prpOpportunityProduct,
        freePrpOpportunityProduct,
        transplantationDefaultProduct,
        prpDefaultProduct,
        bundleDefaultProduct,
        eyebrowSelected,
        eyebrowProduct,
        eyebrowOpportunityProduct,
        eyebrowDefaultProduct,
        beardSelected,
        beardProduct,
        beardOpportunityProduct,
        beardDefaultProduct,
        mesotherapySelected,
        mesotherapyProduct,
        mesotherapyOpportunityProduct,
        freeMesotherapyOpportunityProduct,
        mesotherapyDefaultProduct,
      });
      setSelectedProducts(products);
      hasInitialized.current = true;
      onComplete?.();
    } else if (
      !hasInitialized.current &&
      !isLoading &&
      !loadingOpportunity &&
      !hasOpportunityHairProducts
    ) {
      // If there are no opportunity hair products, mark as completed immediately
      hasInitialized.current = true;
      onComplete?.();
    }
  }, [
    isLoading,
    loadingOpportunity,
    selectedBundles.length,
    transplantOpportunityProduct,
    prpOpportunityProduct,
    freePrpOpportunityProduct,
    eyebrowOpportunityProduct,
    beardOpportunityProduct,
    mesotherapyOpportunityProduct,
    freeMesotherapyOpportunityProduct,
    onComplete,
  ]);
};

export default useInitializeHairProducts;
